import React, { FC, ReactNode, memo } from 'react';
import { useDraggable, useDndContext } from '@dnd-kit/core';
import Stack, { StackProps } from '@mui/material/Stack';
import { GridRowId } from '@mui/x-data-grid-pro';

import { getDraggableId, getDroppableId } from './DataGrid.utils';

interface Props extends Omit<StackProps, 'id'> {
  id: GridRowId;
  field: string;
  draggingSx?: StackProps['sx'];
  disabled?: boolean;
  children?: ReactNode;
}

export const DataGridDragCellWrapper: FC<Props> = memo(
  ({ id, field, draggingSx, disabled, sx, children, ...props }) => {
    const dndContext = useDndContext();

    const droppableNode = dndContext.droppableContainers.get(getDroppableId(id, field))?.node;

    // Draggable
    const {
      listeners,
      attributes,
      setNodeRef: setDraggableNodeRef,
      transform,
    } = useDraggable({ id: getDraggableId(id, field), disabled: disabled });

    const isMoving = transform?.x || transform?.y;

    const draggableSx = isMoving
      ? ({
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          position: 'absolute',
          ...sx,
          ...draggingSx,
        } as StackProps['sx'])
      : sx;

    return (
      <Stack
        ref={setDraggableNodeRef}
        {...listeners}
        {...attributes}
        tabIndex={-1} // Remove focus outline
        alignItems="center"
        height={droppableNode?.current?.clientHeight}
        width={droppableNode?.current?.clientWidth}
        flexGrow={1}
        overflow="hidden"
        sx={{ ...draggableSx }}
        {...props}
      >
        {children}
      </Stack>
    );
  },
);
